import React, { useEffect, useMemo, useRef, useState } from "react";
import { MoreVertical, Trash2 } from "lucide-react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import "./ChatHistorySidebar.scss";
import { fetchChatList, deleteChat, ChatListItem } from "src/services/ChatbotAPI";

export interface ChatSession {
  session_id: string;
  title: string;
  preview?: string;
  created_at: string;
}

interface ChatGroup {
  label: string;
  items: ChatSession[];
}

interface Props {
  isOpen: boolean;
  onToggle: () => void;
  onSelectChat: (chat: ChatSession) => void;
  onChatDeleted?: (sessionId: string) => void;
  currentSessionId: string | null;
}

const groupChatsByDate = (chats: ChatSession[]): ChatGroup[] => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const groups: { [key: string]: ChatSession[] } = {
    Today: [],
    Yesterday: [],
    Older: [],
  };

  chats.forEach(chat => {
    const chatDate = new Date(chat.created_at);
    const dateStr = chatDate.toDateString();

    if (dateStr === today.toDateString()) {groups.Today.push(chat);}
    else if (dateStr === yesterday.toDateString()) {groups.Yesterday.push(chat);}
    else {groups.Older.push(chat);}
  });

  return Object.entries(groups)
    .filter(([_, items]) => items.length > 0)
    .map(([label, items]) => ({
      label,
      items: items.sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ),
    }));
};

export const ChatHistorySidebar: React.FC<Props> = ({
  isOpen,
  onToggle,
  onSelectChat,
  onChatDeleted,
  currentSessionId,
}) => {
  const { t } = useTranslation();
  const [chats, setChats] = useState<ChatSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadChatHistory = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const rawChats: ChatListItem[] = await fetchChatList();
        const mappedChats: ChatSession[] = rawChats.map((chat: any) => ({
          session_id: String(chat.session_id),
          title: chat.title || "AI Chat Session",
          preview: chat.preview,
          created_at: chat.created_at,
        }));

        setChats(mappedChats);
      } catch (err) {
        // eslint-disable-next-line no-console -- intentional error logging
        console.error("Failed to load chat history:", err);
        setError("Failed to load chat history");
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {loadChatHistory();}
  }, [isOpen]);

  // Close the open row menu when clicking anywhere outside of it.
  useEffect(() => {
    if (!openMenuId) {return;}
    const handleOutsideClick = (event: MouseEvent) => {
      if (listRef.current && !listRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [openMenuId]);

  const normalizedTerm = searchTerm.trim().toLowerCase();
  const filteredHistory = useMemo(() => {
    const filtered = !normalizedTerm
      ? chats
      : chats.filter(item => {
          const title = item.title?.toLowerCase() || "";
          const preview = item.preview?.toLowerCase() || "";
          return title.includes(normalizedTerm) || preview.includes(normalizedTerm);
        });

    return groupChatsByDate(filtered);
  }, [chats, normalizedTerm]);

  const handleChatSelect = (chat: ChatSession) => {
    onSelectChat(chat);
  };

  const handleToggleMenu = (event: React.MouseEvent, sessionId: string) => {
    event.stopPropagation();
    setOpenMenuId(prev => (prev === sessionId ? null : sessionId));
  };

  const handleDeleteChat = async (event: React.MouseEvent, chat: ChatSession) => {
    event.stopPropagation();
    if (deletingId) {return;}

    setDeletingId(chat.session_id);
    const response = await deleteChat(chat.session_id);
    setDeletingId(null);
    setOpenMenuId(null);

    if (response.status) {
      setChats(prev => prev.filter(c => c.session_id !== chat.session_id));
      onChatDeleted?.(chat.session_id);
      message.success(response.message);
    } else {
      message.error(response.message);
    }
  };

  return (
    <div className={`chat-history-panel ${isOpen ? "open" : "collapsed"}`}>
      <div className="chat-history-header">
        <h4>{t("chatScreen.history")}</h4>
        <button className="history-close" onClick={onToggle} aria-label={t("chatScreen.closeChatHistory")}>
          x
        </button>
      </div>

      <div className="chat-history-search">
        <input
          type="text"
          value={searchTerm}
          onChange={event => setSearchTerm(event.target.value)}
          placeholder={t("chatScreen.searchChats")}
          aria-label={t("chatScreen.searchChats")}
        />
      </div>

      <div className="chat-history-list" ref={listRef}>
        {isLoading ? (
          <div className="loading-state">{t("chatScreen.loadingChatHistory")}</div>
        ) : error ? (
          <div className="error-state">{error}</div>
        ) : filteredHistory.length === 0 ? (
          <div className="empty-state">
            {normalizedTerm ? "No chats match your search" : "No chat history found"}
          </div>
        ) : (
          filteredHistory.map(group => (
            <div key={group.label} className="chat-history-section">
              <div className="chat-history-group-title">{group.label}</div>
              {group.items.map(chat => (
                <div
                  key={chat.session_id}
                  className={`history-item ${currentSessionId === chat.session_id ? "active" : ""} ${
                    openMenuId === chat.session_id ? "menu-open" : ""
                  }`}
                  onClick={() => handleChatSelect(chat)}
                >
                  <div className="history-text">
                    <div className="history-title">{chat.title || "Untitled Chat"}</div>
                    {chat.preview && <div className="history-sub">{chat.preview}</div>}
                  </div>

                  <div className="history-item-actions">
                    <button
                      type="button"
                      className="history-item-menu-btn"
                      aria-label="Chat options"
                      aria-haspopup="menu"
                      aria-expanded={openMenuId === chat.session_id}
                      onClick={event => handleToggleMenu(event, chat.session_id)}
                    >
                      <MoreVertical size={16} />
                    </button>

                    {openMenuId === chat.session_id && (
                      <div className="history-item-dropdown" role="menu">
                        <button
                          type="button"
                          className="history-dropdown-item danger"
                          role="menuitem"
                          disabled={deletingId === chat.session_id}
                          onClick={event => handleDeleteChat(event, chat)}
                        >
                          <Trash2 size={14} />
                          <span>
                            {deletingId === chat.session_id ? "Deleting..." : "Delete chat"}
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      <div className="chat-history-footer">{t("chatScreen.chatsStoredFor30Days")}</div>
    </div>
  );
};

export default ChatHistorySidebar;
